import {
  X, User, Phone, Mail, Route, Calendar, Package,
  Truck, MapPin, Navigation, Edit, Map
} from 'lucide-react';

import { getStatusConfig, formatCurrency } from '../../../../../utils/utils';
import PaymentTypeIcon from '../../icons/PaymentTypeIcon';
import { getPaymentBg } from '../../background/PaymentBackground';
import MiniMap from '../../location/MiniMap';

const TransportViewModal = ({ show, onClose, transport, onSendLocation, onEditTransport }) => {
  if (!show || !transport) return null;

  // Extract data from different sources - adapt to both detailed and listing data
  const rawData = transport.raw_data || transport;
  const locations = transport.locations || {};
  const clientDetails = transport.client_details || {};
  const contactDetails = transport.contact_details || {};
  
  // Extract vehicle coordinates from transport data
  const vehicleLat = transport.vehicul_lat;
  const vehicleLng = transport.vehicul_lng;
  const vehicleAddress = transport.vehicul_adresa;
  
  // Helper function to get phone numbers correctly
  const getExpeditorPhone = () => {
    // Prioritate: telefon_expeditor din transport direct, apoi din raw_data, apoi telefon client
    return transport.telefon_expeditor || 
           rawData.telefon_expeditor || 
           transport.telefon ||
           clientDetails.telefon || 
           'N/A';
  };

  const getDestinatarPhone = () => {
    // Prioritate: telefon_destinatar din transport direct, apoi din raw_data
    return transport.telefon_destinatar || 
           rawData.telefon_destinatar || 
           'N/A';
  };

  const getDestinatarName = () => {
    // Prioritate: nume_destinatar din transport direct, apoi din raw_data
    return transport.nume_destinatar || 
           rawData.nume_destinatar || 
           null;
  };

  // Extract location data
  const plecare = locations.plecare || {};
  const destinatie = locations.destinatie || {};
  
  // Format dates if they exist
  const formatDateTime = (dateTime) => {
    if (!dateTime) return null;
    try {
      return new Date(dateTime).toLocaleString('ro-RO');
    } catch {
      return dateTime;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4
                bg-white/10 bg-black/20 backdrop-blur-md border border-white/30 rounded-lg shadow-lg">
      <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-800">Detalii Transport - {transport.id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Status & Payment Card */}
        <div
          className={`rounded-xl p-4 mb-6 border shadow-sm transition hover:shadow-md ${getPaymentBg(
            transport.tip_plata
          )}`}
        >
          <div className="flex items-center justify-between gap-4">
            {/* Stânga: Status */}
            <div className="flex items-center min-w-[120px]">
              {getStatusConfig(transport.status).icon}
              <span className="ml-2 font-medium text-sm">{getStatusConfig(transport.status).text}</span>
            </div>

            {/* Mijloc: Tip plată */}
            <div className="flex-1 text-center">
              <div className="flex flex-col items-center justify-center gap-1">
                <PaymentTypeIcon tip_plata={transport.tip_plata} />
                <p className="text-md font-semibold uppercase tracking-wide">
                  {transport.tip_plata || 'N/A'}
                </p>
              </div>
            </div>

            {/* Dreapta: Suma + dată */}
            <div className="text-right min-w-[120px]">
              <p className="text-lg font-bold">{formatCurrency(transport.value)}</p>
              <p className="text-xs text-gray-500">{transport.date}</p>
            </div>
          </div>
        </div>

        {/* Vehicle Location Map */}
        {(vehicleLat && vehicleLng) && (
          <div className="mb-6 space-y-4">
            <h4 className="font-semibold text-gray-800 flex items-center border-b pb-2">
              <Map className="w-5 h-5 mr-2 text-orange-500" />
              Locația Actuală a Vehiculului
            </h4>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="relative w-full h-64 mb-4">
                <MiniMap
                  latitude={vehicleLat}
                  longitude={vehicleLng}
                  zoom={15}
                  className="absolute inset-0 w-full h-full rounded-lg"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {vehicleAddress && (
                  <div className="flex items-start bg-white p-3 rounded-lg">
                    <MapPin className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Adresa</p>
                      <p className="text-gray-600 text-xs">{vehicleAddress}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start bg-white p-3 rounded-lg">
                  <Navigation className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800 text-sm">Coordonate</p>
                    <p className="text-gray-600 text-xs">{vehicleLat}, {vehicleLng}</p>
                  </div>
                </div>

                {transport.vehicul_viteza && (
                  <div className="flex items-start bg-white p-3 rounded-lg">
                    <Truck className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Viteză</p>
                      <p className="text-gray-600 text-xs">{transport.vehicul_viteza} km/h</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Route Information - Enhanced */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 flex items-center border-b pb-2 mb-4">
            <Route className="w-5 h-5 mr-2 text-orange-500" />
            Informații Rută
          </h4>
          
          <div className="space-y-4">
            {/* Departure Location */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h5 className="font-semibold text-green-800">Plecare</h5>
                  <p className="text-sm text-green-700">Punctul de ridicare</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Adresa</p>
                    <p className="text-sm text-green-700">
                      {plecare.adresa_completa || transport.plecare_adresa || transport.startLocation || 'N/A'}
                    </p>
                  </div>
                </div>
                
                {/* Client/Expeditor info */}
                <div className="flex items-start">
                  <User className="w-4 h-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Expeditor</p>
                    <p className="text-sm text-green-700">
                      {clientDetails.nume_firma || transport.client || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-4 h-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Telefon Expeditor</p>
                    <p className="text-sm text-green-700">{getExpeditorPhone()}</p>
                  </div>
                </div>

                {plecare.nume && (
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Oraș</p>
                      <p className="text-sm text-green-700">{plecare.nume}</p>
                    </div>
                  </div>
                )}

                
              </div>
            </div>

          

            {/* Destination Location */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-3">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h5 className="font-semibold text-red-800">Destinație</h5>
                  <p className="text-sm text-red-700">Punctul de livrare</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 text-red-600 mr-2 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Adresa</p>
                    <p className="text-sm text-red-700">
                      {destinatie.adresa_completa || transport.destinatie_adresa || transport.endLocation || 'N/A'}
                    </p>
                  </div>
                </div>
                
                {/* Destinatar info - NUMELE */}
                {getDestinatarName() && (
                  <div className="flex items-start">
                    <User className="w-4 h-4 text-red-600 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Destinatar</p>
                      <p className="text-sm text-red-700">{getDestinatarName()}</p>
                    </div>
                  </div>
                )}

                {/* Telefon destinatar - NUMAI AL DESTINATARULUI */}
                {getDestinatarPhone() !== 'N/A' && (
                  <div className="flex items-start">
                    <Phone className="w-4 h-4 text-red-600 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Telefon Destinatar</p>
                      <p className="text-sm text-red-700">{getDestinatarPhone()}</p>
                    </div>
                  </div>
                )}

                {destinatie.nume && (
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-red-600 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Oraș</p>
                      <p className="text-sm text-red-700">{destinatie.nume}</p>
                    </div>
                  </div>
                )}

               
              </div>
            </div>
          </div>
        </div>

        {/* Grid cu informațiile de bază */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Section title="Informații Client" icon={<User className="w-5 h-5 mr-2 text-orange-500" />}>
            <InfoItem 
              icon={<User className="text-blue-600" />} 
              color="blue" 
              label="Client" 
              value={clientDetails.nume_firma || transport.client} 
            />
            <InfoItem 
              icon={<Mail className="text-purple-600" />} 
              color="purple" 
              label="Email" 
              value={clientDetails.email || transport.email} 
            />
            <InfoItem 
              icon={<Phone className="text-green-600" />} 
              color="green" 
              label="Telefon Client" 
              value={clientDetails.telefon || transport.telefon} 
            />
            {transport.prioritate && (
              <InfoItem 
                icon={<Calendar className="text-orange-600" />} 
                color="orange" 
                label="Prioritate" 
                value={transport.prioritate.charAt(0).toUpperCase() + transport.prioritate.slice(1)} 
              />
            )}
          </Section>

          <Section title="Vehicul și Șofer" icon={<Truck className="w-5 h-5 mr-2 text-orange-500" />}>
            <InfoItem icon={<Truck className="text-blue-600" />} color="blue" label="Vehicul" value={transport.vehicle} />
            <InfoItem icon={<User className="text-green-600" />} color="green" label="Șofer" value={transport.driver} />
            {transport.progres !== undefined && (
              <InfoItem 
                icon={<Route className="text-purple-600" />} 
                color="purple" 
                label="Progres" 
                value={`${transport.progres}%`} 
              />
            )}
          </Section>
        </div>

        {/* Description */}
        {transport.description && (
          <div className="space-y-4 mb-6">
            <h4 className="font-semibold text-gray-800 flex items-center border-b pb-2">
              <Package className="w-5 h-5 mr-2 text-orange-500" />
              Descrierea Transportului
            </h4>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">{transport.description}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
          {transport.status === 'pe_drum' && (
            <button
              onClick={() => {
                onClose();
                onSendLocation(transport);
              }}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-colors flex items-center justify-center"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Trimite Locația
            </button>
          )}
          <button
            onClick={() => {
              onClose();
              onEditTransport(transport);
            }}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-colors flex items-center justify-center"
            >
            <Edit className="w-4 h-4 mr-2" />
            Editează
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors"
          >
            Închide
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransportViewModal;

// Reusable Components
const Section = ({ title, icon, children }) => (
  <div className="space-y-4">
    <h4 className="font-semibold text-gray-800 flex items-center border-b pb-2">{icon}{title}</h4>
    <div className="space-y-3">{children}</div>
  </div>
);

const InfoItem = ({ icon, color, label, value }) => (
  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
    <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center mr-3`}>
      {icon}
    </div>
    <div>
      <p className="font-medium text-gray-900">{value || 'N/A'}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);